import React, { useCallback, useState } from "react";
import AccordionBox from "components/Layouts/AccordionBox";
import ConfirmDialog from "components/Elements/ConfirmDialog";
import { CircularProgress, Box } from "@mui/material";
import ScheduleReportModal from "../ScheduleReportModal";
import { useScheduleReportModal } from "../hooks/useScheduleReportModal";
import { useReportSubscriptions } from "../hooks/useReportSubscriptions";
import { formValuesToApiRequest } from "services/reportSubscriptions";
import ReportSubscriptionsExplanation from "./ReportSubscriptionsExplanation";
import ReportSubscriptionsEmptyState from "./ReportSubscriptionsEmptyState";
import ReportSubscriptionsList from "./ReportSubscriptionsList";

// Page-level glue for the profile subscriptions card. Loads subscriptions
// from the API via useReportSubscriptions and forwards user actions to the
// hook's create/update/toggle/remove operations.
const ReportSubscriptionsCard = () => {
	const { subscriptions, loading, update, toggle, remove } = useReportSubscriptions();
	const [pendingDelete, setPendingDelete] = useState(null);
	const editModal = useScheduleReportModal();

	const handleToggle = useCallback((id) => toggle(id), [toggle]);

	const handleDeleteRequest = useCallback(
		(id) => {
			const sub = subscriptions.find((s) => s.id === id);
			if (sub) setPendingDelete(sub);
		},
		[subscriptions]
	);

	const cancelDelete = useCallback(() => setPendingDelete(null), []);

	const confirmDelete = useCallback(async () => {
		if (!pendingDelete) return;
		const ok = await remove(pendingDelete.id);
		if (ok) setPendingDelete(null);
	}, [pendingDelete, remove]);

	const handleEdit = useCallback(
		(subscription) => {
			editModal.openModal({
				name: subscription.name,
				reportType: subscription.reportType,
				outputFormat: subscription.outputFormat,
				frequency: subscription.frequency,
				daysOfWeek: subscription.daysOfWeek,
				timeOfDay: subscription.timeOfDay,
				timezone: subscription.timezone,
				deliveryEmail: subscription.deliveryEmail,
				_id: subscription.id,
				_reportName: subscription.reportName,
				_filterContext: subscription.filterContext,
				_filterSummary: subscription.filters,
			});
		},
		[editModal]
	);

	const handleEditSubmit = useCallback(
		async (payload) => {
			const id = editModal.initialValues?._id;
			const filterContext = editModal.initialValues?._filterContext;
			const filterSummary = editModal.initialValues?._filterSummary;
			const apiBody = formValuesToApiRequest(payload, filterContext, filterSummary);
			await update(id, apiBody);
		},
		[editModal.initialValues, update]
	);

	const editingReports = editModal.initialValues
		? [
				{
					id: editModal.initialValues._id,
					name: editModal.initialValues._reportName,
					reportType: editModal.initialValues.reportType,
					formats: [editModal.initialValues.outputFormat],
				},
		  ]
		: [];

	// In edit mode the report itself is fixed (changing it would require
	// re-capturing filterContext from the originating page), and the format
	// is constrained to whatever the existing subscription was created with.
	const editingFormatsForReport = (reportType) =>
		editingReports.find((r) => r.reportType === reportType)?.formats;

	return (
		<>
			<AccordionBox title="Report Subscriptions" noExpand>
				<ReportSubscriptionsExplanation />
				{loading ? (
					<Box display="flex" justifyContent="center" p={3}>
						<CircularProgress size={24} />
					</Box>
				) : subscriptions.length === 0 ? (
					<ReportSubscriptionsEmptyState />
				) : (
					<ReportSubscriptionsList
						subscriptions={subscriptions}
						onToggle={handleToggle}
						onEdit={handleEdit}
						onDelete={handleDeleteRequest}
					/>
				)}
			</AccordionBox>
			{editModal.open ? (
				<ScheduleReportModal
					open
					onClose={editModal.closeModal}
					availableReports={editingReports}
					availableFormatsForReport={editingFormatsForReport}
					initialValues={editModal.initialValues || undefined}
					mode="edit"
					lockReportSelection
					onSubmit={handleEditSubmit}
				/>
			) : null}
			<ConfirmDialog
				open={!!pendingDelete}
				title="Delete subscription?"
				message={
					pendingDelete
						? `“${pendingDelete.name || pendingDelete.reportName}” will stop being delivered. This cannot be undone.`
						: ""
				}
				confirmButtonText="Delete"
				onConfirm={confirmDelete}
				onCancel={cancelDelete}
			/>
		</>
	);
};

export default ReportSubscriptionsCard;
